import { ArrowUpRight, LifeBuoy, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'

/**
 * CrossRefBanner — surface a cross-reference from one IntelliAuth surface
 * to another. Two intended uses:
 *
 *   1. On the central docs site, on any page that's also surfaced as
 *      in-product inline-help. Banner reads "Available inline in product"
 *      and links the reader back to `<app>.<DOMAIN_BASE><path>`.
 *
 *   2. Inside a HelpBubblePopover footer (alternative layout) when the
 *      "Open in sidebar" affordance isn't appropriate.
 *
 *   Pencil: derived from the docs site article-page right rail
 *   (Vzk9b/sSdDv) and the cross-reference contract diagram. Visually
 *   echoes Alert/Info but adds an inline action with arrow.
 */
export interface CrossRefBannerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Eyebrow text — uppercase, mono. Default: "AVAILABLE INLINE IN PRODUCT". */
  eyebrow?: string
  /** Primary message. */
  title: React.ReactNode
  /** Secondary supporting text. */
  description?: React.ReactNode
  /** Action label + href. Renders as a right-aligned link with an arrow. */
  action: { label: string; href: string }
  /** Icon shown on the left. Default: LifeBuoy. */
  icon?: LucideIcon
  /** External link → opens in a new tab. Default `true`. */
  external?: boolean
}

export const CrossRefBanner = React.forwardRef<HTMLDivElement, CrossRefBannerProps>(
  (
    {
      className,
      eyebrow = 'ALSO INSIDE THE PRODUCT',
      title,
      description,
      action,
      icon = LifeBuoy,
      external = true,
      ...props
    },
    ref,
  ) => (
    <section
      ref={ref as React.Ref<HTMLElement>}
      aria-label={typeof title === 'string' ? title : 'Cross-reference'}
      className={cn(
        'flex items-start gap-3 rounded-md border px-4 py-3',
        'bg-ia-info-bg border-ia-info/20 text-ia-text',
        className,
      )}
      {...(props as React.HTMLAttributes<HTMLElement>)}
    >
      <div className="shrink-0 pt-0.5 text-ia-info">
        <Icon icon={icon} size="md" />
      </div>
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="text-[10px] font-mono font-semibold tracking-[0.12em] text-ia-text-tertiary uppercase">
            {eyebrow}
          </div>
        )}
        <div className="text-body-sm font-semibold text-ia-text mt-1">{title}</div>
        {description && (
          <div className="text-body-sm text-ia-text-secondary mt-1">{description}</div>
        )}
      </div>
      <a
        href={action.href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className={cn(
          'shrink-0 inline-flex items-center gap-1.5',
          'text-body-sm font-semibold text-ia-info hover:underline',
          'self-center',
        )}
      >
        {action.label}
        <Icon icon={ArrowUpRight} size="xs" />
      </a>
    </section>
  ),
)
CrossRefBanner.displayName = 'CrossRefBanner'
